import * as React from 'react'
import { ArrowUpRight, ChevronDown, MapPin, MessageSquare, X } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  useBatchUpdateCustomAgentResultsMutation,
  type CustomAgentTask
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../components/ui/button'
import { TaskPriority } from '../../components/primitives/TaskPriority'
import { TaskCompletionCircle } from '../../components/primitives/TaskCompletion'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../components/ui/collapsible'
import { runPageHref } from '../../lib/component-links'
import { useIsGlobalAdmin } from '../../lib/global-admin'
import { useChatHandoff } from '../../lib/use-chat-handoff'
import { cn } from '../../lib/utils'
import {
  taskDetail,
  taskItemId,
  taskPriority,
  taskStatus,
  taskTitle,
  type TaskRunGroup,
  type TaskStatus
} from './tasks-helpers'

const STATUS_LABEL = { open: 'Open', done: 'Done', rejected: 'Rejected' }
export function ReportTasks({
  groups,
  status,
  selectedId,
  onSelect,
  onUpdate
}: {
  groups: TaskRunGroup[]
  status: TaskStatus
  selectedId: string | null
  onSelect: (id: string) => void
  onUpdate: (tasks: CustomAgentTask[]) => void
}): React.ReactElement | null {
  const headingId = React.useId()
  const count = groups.reduce((total, group) => total + group.counts[status], 0)
  if (!groups.length) return null
  return (
    <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-2">
      <header className="flex items-center gap-2 px-1">
        <MapPin size={14} strokeWidth={1.5} className="shrink-0 text-ink-3" aria-hidden="true" />
        <h2 id={headingId} className="m-0 min-w-0 text-13 font-medium text-ink-2">
          Location not set
        </h2>
        <span
          className="ml-0.5 shrink-0 text-12 leading-4 tabular-nums text-ink-3"
          aria-label={`${count} ${STATUS_LABEL[status].toLowerCase()} tasks`}
        >
          {count}
        </span>
      </header>
      <div className="flex min-w-0 flex-col gap-3">
        {groups.map((group) => (
          <RunTasks
            key={group.key}
            group={group}
            status={status}
            selectedId={selectedId}
            onSelect={onSelect}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </section>
  )
}

function RunTasks({
  group,
  status,
  selectedId,
  onSelect,
  onUpdate
}: {
  group: TaskRunGroup
  status: TaskStatus
  selectedId: string | null
  onSelect: (id: string) => void
  onUpdate: (tasks: CustomAgentTask[]) => void
}): React.ReactElement {
  const headingId = React.useId()
  const { tasks, counts } = group
  return (
    <section
      aria-labelledby={headingId}
      className="min-w-0 overflow-hidden rounded-lg border border-line/80 bg-card"
    >
      <header className="flex items-center justify-between gap-4 border-b border-line/70 bg-bg-sunk/35 px-3 py-2.5">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 id={headingId} className="m-0 text-14 leading-5 font-semibold text-ink">
            Problem not linked
          </h3>
          <span className="text-12 text-ink-3">{tasks[0]?.config_name}</span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex flex-col items-end gap-1 text-12 tabular-nums text-ink-3">
            <span>
              {status === 'rejected'
                ? `${counts.rejected} rejected`
                : `${counts.done}/${counts.open + counts.done} done`}
            </span>
          </div>
        </div>
      </header>
      <ol className="m-0 flex list-none flex-col divide-y divide-line/60 p-0">
        {tasks.map((task) => (
          <TaskRow
            key={taskItemId(task)}
            task={task}
            selected={task.id === selectedId}
            onSelect={() => onSelect(task.id)}
            onUpdate={() => onUpdate([task])}
          />
        ))}
      </ol>
    </section>
  )
}

function TaskRow({
  task,
  selected,
  onSelect,
  onUpdate
}: {
  task: CustomAgentTask
  selected: boolean
  onSelect: () => void
  onUpdate: () => void
}): React.ReactElement {
  const title = taskTitle(task)
  const status = taskStatus(task)
  const done = status === 'done'
  const rejected = status === 'rejected'
  const [batchUpdate, { isLoading: updating, error }] = useBatchUpdateCustomAgentResultsMutation()
  const saving = updating
  const rowRef = React.useRef<HTMLLIElement>(null)
  React.useEffect(() => {
    if (selected) rowRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [selected])
  const complete = async (): Promise<void> => {
    const result = await batchUpdate({
      item_ids: [taskItemId(task)],
      work_status: done ? 'open' : 'done'
    })
    if ('data' in result) onUpdate()
  }
  return (
    <Collapsible asChild open={selected} onOpenChange={onSelect}>
      <li
        ref={rowRef}
        value={task.position + 1}
        className={cn('transition-colors duration-150', selected && 'bg-bg-sunk/25')}
      >
        <div
          className={cn(
            'grid grid-cols-[1.25rem_1rem_minmax(0,1fr)_4rem_1rem] items-center gap-x-1.5 gap-y-1 px-3 py-2.5 transition-colors hover:bg-bg-sunk/45',
            selected && 'bg-bg-sunk/40'
          )}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-label={title}
              aria-current={selected || undefined}
              className="col-span-4 col-start-2 row-start-1 grid grid-cols-subgrid items-center rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
            >
              <span
                aria-hidden="true"
                className="col-start-1 row-start-1 text-right text-12 tabular-nums text-ink-3"
              >
                {task.position + 1}.
              </span>
              <span
                className={cn(
                  'col-start-2 row-start-1 min-w-0 break-words text-14 leading-5 font-medium',
                  done || rejected ? 'text-ink-3' : 'text-ink'
                )}
              >
                {title}
              </span>
              <span className="col-start-3 row-start-1 justify-self-end pr-1.5">
                <TaskPriority priority={taskPriority(task)} emptyLabel="No priority" />
              </span>
              <ChevronDown
                size={14}
                aria-hidden="true"
                className={cn(
                  'col-start-4 row-start-1 justify-self-end text-ink-3 transition-transform duration-200 motion-reduce:transition-none',
                  selected && 'rotate-180'
                )}
              />
            </button>
          </CollapsibleTrigger>
          {rejected ? (
            <X
              size={15}
              className="col-start-1 row-start-1 text-st-down dark:text-red-300"
              aria-label="Rejected"
            />
          ) : (
            <TaskCompletionCircle
              title={title}
              done={done}
              saving={saving}
              onClick={() => void complete()}
              className="col-start-1 row-start-1"
            />
          )}
        </div>
        {error && (
          <p role="alert" className="m-0 px-3 pb-3 text-12 text-st-down">
            Couldn't update the task. Try again.
          </p>
        )}
        <CollapsibleContent>
          <TaskDetail task={task} saving={saving} />
        </CollapsibleContent>
      </li>
    </Collapsible>
  )
}

function TaskDetail({
  task,
  saving
}: {
  task: CustomAgentTask
  saving: boolean
}): React.ReactElement {
  const showRunLink = useIsGlobalAdmin()
  const { askInChat, rejectInChat } = useChatHandoff()
  const detail = taskDetail(task)
  const title = taskTitle(task)
  return (
    <div className="min-w-0 px-3 pb-3 pt-1">
      {detail && (
        <p className="m-0 mb-2.5 whitespace-pre-wrap break-words rounded-md border border-line bg-bg-elev px-3 py-2.5 text-13 leading-relaxed text-ink-2">
          {detail}
        </p>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            variant="outline"
            onClick={() =>
              askInChat({
                title,
                context: {
                  source: 'legacy_result',
                  resultItemId: taskItemId(task),
                  runId: task.run_id
                }
              })
            }
            className="h-7 rounded border-brand/20 bg-brand/5 px-2.5 text-12 font-medium text-brand shadow-none hover:bg-brand/10 hover:text-brand dark:border-sky-400/20 dark:bg-sky-400/5 dark:text-sky-300 dark:hover:text-sky-300"
          >
            <MessageSquare size={14} />
            Ask in chat
          </Button>
          {taskStatus(task) !== 'rejected' && (
            <Button
              size="xs"
              variant="outline"
              disabled={saving}
              onClick={() => rejectInChat({ title, itemIds: [taskItemId(task)] })}
              className="h-7 rounded border-st-down/20 bg-st-down-bg/40 px-2.5 text-12 font-medium text-st-down shadow-none hover:bg-st-down-bg hover:text-st-down dark:border-red-400/20 dark:bg-red-400/5 dark:text-red-300 dark:hover:text-red-300"
            >
              <X size={14} />
              Reject
            </Button>
          )}
        </div>
        <div className="flex items-center gap-4 text-12 font-medium text-brand dark:text-sky-300">
          {showRunLink && (
            <Link
              to={runPageHref('custom', task.run_id)}
              className="inline-flex items-center gap-1 rounded outline-none hover:underline focus-visible:ring-2 focus-visible:ring-brand"
            >
              Open run
              <ArrowUpRight size={14} aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}
